import type { MappedLeadData } from '../../types';
import { leadToReport } from '../../integrations/reports/reportDataAdapter';
import { renderSqlReport } from './sql-report';
import { DEFAULT_CONFIG, type SectionConfig } from './section-catalog';

/**
 * Admin demo data — a complete, fictional lead used ONLY by the Report Template
 * editor preview. It exercises the same adapter + renderer as production, so
 * what the admin sees is exactly what a real report produces.
 *
 * Fictional company and person; never mixed with real lead data.
 */
const DEMO_ENVELOPE = {
  lvl2: {
    id: 'demo-lvl2-1',
    lvl1_id: 'demo-lvl1-1',
    campaign_id: 'demo-campaign',
    status: 'ENGAGED',
    icp_score: 87,
    overview:
      'Alex leads operations across three manufacturing sites at Northstar. The company is standardising its production reporting after a recent acquisition, while each site still relies on a different combination of spreadsheets and local tools. Discovery confirmed a practical priority: give site managers one reliable view of production performance without replacing the systems already running on the factory floor.',
    personalization_strategy: {
      opening_approach:
        'Start with the reporting workflow discussed in discovery: who collects the numbers, how long reconciliation takes, and which decisions are delayed. Use their current process as the demonstration baseline.',
      call_to_action:
        'Propose a focused demonstration using one production line, with the operations analytics lead present and clear success measures agreed up front.',
      talking_points: [
        'Their current reconciliation process takes several days each month.',
        'Site managers already track downtime locally, but never in one place.',
        'The integration review gives a natural deadline for a decision.',
      ],
      avoid_language: ['ERP replacement', 'Big-bang rollout', 'Savings before measurement'],
      channel_priority: 'Direct email, followed by a scheduled call',
    },
    icp: {
      justification:
        'Alex owns operational performance and the integration of newly acquired sites. They favour small, measurable changes that can be adopted by local teams before a wider rollout.',
      top_pain: 'Site-level reporting is slow to reconcile, limiting visibility into downtime and production exceptions.',
      value_hook: 'A shared reporting layer could reduce manual consolidation and help managers act on the same information.',
      trigger_event: 'An integration review is underway, with a shortlist expected before the next quarterly planning cycle.',
      ICP_fit: 'Prime',
      confidence_score: '92',
    },
    opportunities: [
      { category: 'Reporting pilot', need: 'One production line with agreed success measures and a named reporting owner.', urgency: 'HIGH' },
      { category: 'Site expansion', need: 'Extend the same reporting layer to the remaining sites once the pilot proves useful.', urgency: 'MEDIUM' },
    ],
    notes: {
      notes: [
        { id: 'n1', created_at: '2026-09-02T08:15:00Z', created_by_name: 'System', title: 'Lead enriched', body: 'Lead and company intelligence became available for the campaign.', activity_type: 'status_change' },
        { id: 'n2', created_at: '2026-09-09T10:00:00Z', created_by_name: 'Taylor Brooks', title: 'Discovery call completed', body: 'Confirmed manual consolidation across three sites. Alex wants an initial pilot on one production line and asked for IT to review the proposed data access.', activity_type: 'note' },
        { id: 'n3', created_at: '2026-09-11T14:40:00Z', created_by_name: 'Taylor Brooks', title: 'Final interaction summary', body: 'Alex confirmed the reporting problem and is willing to sponsor a narrowly scoped pilot. The next meeting should demonstrate one production-line workflow with IT and the analytics lead present. Qualification is supported by a clear operational need and an engaged sponsor; budget, technical access and the decision date still require confirmation.', activity_type: 'interaction_summary' },
      ],
    },
  },
  lvl1_person: {
    id: 'demo-lvl1-1',
    name: 'Alex Morgan',
    title: 'Chief Operating Officer',
    role_position: 'Chief Operating Officer',
    location: 'Manchester, United Kingdom',
    city: 'Manchester',
    country: 'United Kingdom',
    email_id: 'alex.morgan@northstarmfg.example',
    phone_id: '+44 161 496 0100',
    linkedin_id: 'https://www.linkedin.com/in/demo-alex-morgan',
    summary: {
      executive_overview: 'Operations leader driving multi-site standardisation after a recent acquisition.',
      professional_position: {
        decision_authority: 'Final approver',
        influence_level: 'High',
        key_responsibilities: [
          'Multi-site operations and integration',
          'Performance reporting standardisation',
          'Supplier and logistics oversight',
        ],
      },
      professional_profile: {
        overview: 'Alex owns operational performance and the integration of newly acquired sites, favouring small measurable changes adopted by local teams before wider rollout.',
        key_expertise: ['Manufacturing operations', 'Integration planning', 'Performance reporting', 'Lean improvement'],
      },
    },
    profile_intelligence: {
      psychological_profile: {
        buyer_archetype: { primary_type: 'Operational sponsor', description: 'Prefers proposals grounded in measurable operational outcomes rather than platform features.' },
        decision_role: 'Operational sponsor',
        decision_making_style: 'Evidence-led, consultative, incremental',
        mbti_type: 'ISTJ',
        mbti_communication_style: 'Fact-based, structured, quality-focused',
        generation: 'Generation X',
        communication_norms: 'Direct, respectful, expects prepared specifics',
        information_absorption_style: 'Reads detailed documentation before meetings; values written follow-ups',
        influence_principles: {
          authority: 'Responds to peer references from comparable manufacturers',
          social_proof: 'Wants to know who else runs the same reporting model',
          reciprocity: 'Appreciates a prepared pilot scope without obligation',
          liking: 'Values straightforward, low-pressure conversation',
          scarcity: 'Motivated by the integration review window',
          commitment_consistency: 'Prefers a scoped pilot before any wider commitment',
        },
        behavioral_patterns: ['Asks for written detail', 'Consults IT early', 'Decides once evidence is complete'],
      },
      social_intelligence: {
        content_summary: {
          hot_topics: ['Multi-site visibility', 'Downtime reduction', 'Integration after acquisition'],
          pain_themes: ['Manual reconciliation', 'Fragmented local tools', 'Delayed decision-making'],
          aspiration_themes: ['One reliable operational picture', 'Faster exception response'],
        },
        icebreaker_intelligence: {
          tier_1_hooks: [{ opening_line: 'Which production question takes the longest to answer today?', transition: 'That is the question the pilot is designed to shorten.' }],
          tier_2_hooks: [{ opening_line: 'Most multi-site teams reconcile the same numbers in three different ways.', transition: 'A shared layer removes that duplication without replacing anything.' }],
          tier_3_hooks: [],
          avoid_topics: ['Replacing their ERP', 'Promising savings before measuring', 'Large programme rollouts'],
        },
        digital_footprint: ['Active on LinkedIn in industry manufacturing groups', 'Quoted in a regional manufacturing publication'],
      },
      temporal_intelligence: {
        optimal_send_times: [
          { day: 'Tuesday', time: '09:00', timezone: 'Europe/London' },
          { day: 'Thursday', time: '14:00', timezone: 'Europe/London' },
        ],
      },
      cognitive_bias_profile: {
        predicted_objections: [
          { objection: 'Our sites all work differently.', counter_strategy: 'Show how the pilot preserves local processes while agreeing a small common set of measures.' },
          { objection: 'IT is already at capacity.', counter_strategy: 'Document the access needed for the pilot and review it with IT before proposing a delivery date.' },
        ],
      },
      network_intelligence: {
        internal_network: {
          department_colleagues: [
            { name: 'Jamie Patel', title: 'Head of IT', decision_influence: 'high' },
            { name: 'Casey Reed', title: 'Operations Analytics Lead', decision_influence: 'moderate' },
          ],
        },
        external_network: [{ name: 'Regional Manufacturing Forum', role: 'Industry association membership' }],
      },
    },
    opportunities: [
      { category: 'Operational reporting', need: 'Reduce monthly reconciliation effort across three sites.', urgency: 'HIGH' },
    ],
  },
  lvl1_company: {
    id: 'demo-company-1',
    name: 'Northstar Manufacturing',
    industry: 'Industrial manufacturing',
    employees: '250-500',
    website: 'https://northstarmfg.example',
    location: 'Manchester, United Kingdom',
    description: 'Northstar produces specialist components for industrial customers. Its three UK sites have established local reporting practices, creating an opportunity to standardise management information.',
    overview: 'Northstar produces specialist components for industrial customers. Its three UK sites have established local reporting practices, creating an opportunity to standardise management information.',
  },
  campaign: {
    name: 'Operational excellence · UK manufacturing',
    summary: 'Identify manufacturing teams that need a clearer operational picture across multiple sites. The offer is a scoped reporting pilot, with agreed success measures and a path to expand only if the first deployment proves useful.',
    objective: 'Book scoped reporting pilots with multi-site manufacturers.',
  },
  appwrite_user: { prefs: { language: 'en-GB', timezone: 'Europe/London' } },
};

export let lastDemoPayload: any;
export interface DemoRenderResult {
  html: string;
  sectionCount: number;
}

/** Render the demo report with a given section config — same path as production. */
export function renderAdminDemo(config: SectionConfig | undefined, template: 'lead-sql' | 'lead-mql'): DemoRenderResult {
  const variant = template === 'lead-mql' ? 'mql' : 'sql';
  const active = config ?? DEFAULT_CONFIG;
  let __payload: any;
  const { report } = leadToReport(
    {
      // The adapter reads the n8n envelope from lead.rawOriginalData — wrap it
      // exactly as the real get_lead transform does.
      lead: {
        id: 'demo-lvl2-1',
        campaignId: 'demo-campaign',
        campaignName: DEMO_ENVELOPE.campaign.name,
        leadName: DEMO_ENVELOPE.lvl1_person.name,
        leadRole: DEMO_ENVELOPE.lvl1_person.title,
        companyName: DEMO_ENVELOPE.lvl1_company.name,
        status: 'ENGAGED',
        rawOriginalData: DEMO_ENVELOPE,
        personLvl2: DEMO_ENVELOPE.lvl2,
        personLvl1: DEMO_ENVELOPE.lvl1_person,
        companyLvl1: DEMO_ENVELOPE.lvl1_company,
      } as unknown as MappedLeadData,
      branding: { name: 'Brainiacs', color: '#7C4DFF' },
      campaignName: DEMO_ENVELOPE.campaign.name,
      timezone: 'Europe/London',
      capturedAt: new Date().toISOString(),
    },
    variant,
    active,
  );
  lastDemoPayload = report;
  const html = renderSqlReport(report, {
    language: 'en',
    mql: variant === 'mql',
    showActivity: active.sections.includes('activity-timeline'),
    showSummary: active.sections.includes('interaction-summary'),
  });
  return { html, sectionCount: report.sections.length };
}
